import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

// Shared calendar state and navigation logic for appointments and reservations.
// Single source of truth: currentDate + viewMode, mirrored into the URL (?view=&date=)
// so browser back/forward steps through calendar navigation.
const VIEW_MODES = ['day', 'week', 'month'];

function formatDateForUrl(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDateFromUrl(value) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  const parsed = new Date(Number(y), Number(m) - 1, Number(d));
  return isNaN(parsed.getTime()) ? null : parsed;
}

export const useSalonCalendarState = (initialView = 'week') => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentDate, setCurrentDate] = useState(() => {
    return parseDateFromUrl(searchParams.get('date')) || new Date();
  });
  const [viewMode, setViewMode] = useState(() => {
    const urlView = searchParams.get('view');
    return VIEW_MODES.includes(urlView) ? urlView : initialView;
  });

  // Writes view+date into the URL together, pushing a new history entry so
  // browser back/forward can step through calendar navigation.
  const pushCalendarState = useCallback((date, view, options = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', view);
      next.set('date', formatDateForUrl(date));
      return next;
    }, options);
  }, [setSearchParams]);

  // Reconcile local state when the URL changes from outside a handler here
  // (browser back/forward, or a shared/reloaded link) - a no-op after a normal
  // click, since the handler already updated state directly.
  useEffect(() => {
    const urlView = searchParams.get('view');
    if (VIEW_MODES.includes(urlView) && urlView !== viewMode) {
      setViewMode(urlView);
    }
    const urlDate = parseDateFromUrl(searchParams.get('date'));
    if (urlDate && urlDate.getTime() !== currentDate.getTime()) {
      setCurrentDate(urlDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reconcile when the URL itself changes
  }, [searchParams]);

  // Week starts on Monday
  const getWeekDates = useCallback(
    (date) => {
      const base = date || currentDate;
      const startOfWeek = new Date(base);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday start
      startOfWeek.setDate(diff);

      const weekDates = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(startOfWeek);
        d.setDate(startOfWeek.getDate() + i);
        weekDates.push(d);
      }
      return weekDates;
    },
    [currentDate]
  );

  const getMonthDates = useCallback(
    (date) => {
      const base = date || currentDate;
      const year = base.getFullYear();
      const month = base.getMonth();
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      // Adjust to start from Monday (0 = Monday, 6 = Sunday)
      const dayOfWeek = (firstDay.getDay() + 6) % 7;
      startDate.setDate(startDate.getDate() - dayOfWeek);

      const dates = [];
      const current = new Date(startDate);
      for (let i = 0; i < 42; i++) {
        // 6 weeks
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      return dates;
    },
    [currentDate]
  );

  // Navigation always changes only currentDate; views derive their weeks/months from it.
  const navigateDay = useCallback(
    (direction) => {
      const next = new Date(currentDate);
      next.setDate(next.getDate() + direction);
      setCurrentDate(next);
      pushCalendarState(next, viewMode);
    },
    [currentDate, viewMode, pushCalendarState]
  );

  const navigateWeek = useCallback(
    (direction) => {
      const next = new Date(currentDate);
      next.setDate(next.getDate() + direction * 7);
      setCurrentDate(next);
      pushCalendarState(next, viewMode);
    },
    [currentDate, viewMode, pushCalendarState]
  );

  const navigateMonth = useCallback(
    (direction) => {
      const next = new Date(currentDate);
      next.setMonth(next.getMonth() + direction);
      setCurrentDate(next);
      pushCalendarState(next, viewMode);
    },
    [currentDate, viewMode, pushCalendarState]
  );

  const goToToday = useCallback(() => {
    const today = new Date();
    setCurrentDate(today);
    pushCalendarState(today, viewMode);
  }, [viewMode, pushCalendarState]);

  const goToDate = useCallback((date) => {
    let parsed = null;
    if (date instanceof Date) {
      parsed = new Date(date);
    } else if (typeof date === 'string') {
      const candidate = new Date(date);
      if (!isNaN(candidate.getTime())) {
        parsed = candidate;
      }
    }
    if (parsed) {
      setCurrentDate(parsed);
      pushCalendarState(parsed, viewMode);
    }
  }, [viewMode, pushCalendarState]);

  // View switches do NOT change currentDate
  const handleSwitchToDayView = useCallback(() => {
    setViewMode('day');
    pushCalendarState(currentDate, 'day');
  }, [currentDate, pushCalendarState]);

  const handleSwitchToWeekView = useCallback(() => {
    setViewMode('week');
    pushCalendarState(currentDate, 'week');
  }, [currentDate, pushCalendarState]);

  const handleSwitchToMonthView = useCallback(() => {
    setViewMode('month');
    pushCalendarState(currentDate, 'month');
  }, [currentDate, pushCalendarState]);

  // Applies the salon's default calendar view (from settings) - but only when the URL
  // doesn't already carry an explicit view, so this doesn't clobber a view the user
  // picked (or one restored via back/forward) every time it's called on mount/resize.
  const applyDefaultViewFromSettings = useCallback((view) => {
    if (!VIEW_MODES.includes(view) || searchParams.get('view')) return;
    setViewMode(view);
    pushCalendarState(currentDate, view, { replace: true });
  }, [searchParams, currentDate, pushCalendarState]);

  // Shared helper to compute fetch date range based on current state
  const getFetchDateRange = useCallback(
    () => {
      let startDate;
      let endDate;

      if (viewMode === 'day') {
        startDate = new Date(currentDate);
        endDate = new Date(currentDate);
      } else if (viewMode === 'week') {
        const weekDates = getWeekDates(currentDate);
        startDate = weekDates[0];
        endDate = weekDates[6];
      } else if (viewMode === 'month') {
        const monthDates = getMonthDates(currentDate);
        startDate = monthDates[0];
        endDate = monthDates[monthDates.length - 1];
      }

      return { startDate, endDate };
    },
    [viewMode, currentDate, getWeekDates, getMonthDates]
  );

  return {
    currentDate,
    viewMode,
    applyDefaultViewFromSettings,
    getWeekDates,
    getMonthDates,
    navigateDay,
    navigateWeek,
    navigateMonth,
    goToToday,
    goToDate,
    handleSwitchToDayView,
    handleSwitchToWeekView,
    handleSwitchToMonthView,
    getFetchDateRange,
  };
};
